import { html, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { renderNumber, renderText } from '../../text';
import { translateFn } from '../../../../utils/translator.helper';
import { RmCompanyState, RmCompanyStatePayload } from '@nirvana/shared/dtos/types/widgets/common';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { ColumnKeys } from '../../../misconfigurationsApp/types';
import { ScoreLevels, State } from '../../../common/constants';
import { getTextForScore } from '../../../common/customRenderers';
import { getBulletColorForScore } from '../../../common/utils';
import { Target } from '../../../../ui/gzLinkComponent/types';
import { ConsoleRequest, DataProviderType } from '../../../../utils/request';
import { classMap } from 'lit/directives/class-map.js';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-company-state-widget
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-rm-company-state-widget')
export class GzRmCompanyStateWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmCompanyStatePayload;

  @state()
  identityMonitoringToggle = true;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  willUpdate(properties: PropertyValues) {
    super.willUpdate(properties);

    if (properties.has('payload')) {
      // willUpdate in the parent class already set payload to this object
      this.identityMonitoringToggle = !(this.payload.companyState?.identityMonitoringDisabled ?? false);
    }
  }

  protected renderLoader(): TemplateResult {
    return html`
      <div class="d-flex flex-column loading-state-wrapper">
        <div class="${classMap({ 'd-flex align-items-center': true, 'flex-column': this.isWidgetSmall() })}">
          ${this.renderScoreGaugeLoader()} ${this.renderScoreBreakdownLoader()}
        </div>
        <div>${this.renderDistributionLoader()}</div>
      </div>
    `;
  }

  protected renderContent(): TemplateResult {
    const companyState = this.payload?.companyState;

    if (!companyState) {
      return html``;
    }

    return html` <div class="d-flex flex-column">
      <div class="${classMap({ 'd-flex': true, 'flex-column': this.isWidgetSmall() })}">
        ${this.renderScoreGaugeContent(companyState)} ${this.renderScoreBreakdownContent(companyState)}
      </div>
      <div class="mb-2">${this.renderCompanySettings()}</div>
      <div>${this.renderDistributionContent(companyState)}</div>
    </div>`;
  }

  private isWidgetSmall(): boolean {
    return this.currentWidthInterval[1] === this.resizeBreakingPoints[0];
  }

  protected renderScoreGaugeLoader() {
    return html`
      <gz-multi-gauge
        value=""
        disabled="true"
        hasLabels
        class="${classMap({ 'mb-2': true, 'mr-2': !this.isWidgetSmall() })}"
        percentageUnit=""
      ></gz-multi-gauge>
    `;
  }

  protected renderScoreBreakdownLoader() {
    return html` <gz-loader rows="5" columns="1" style="margin-top: calc(var(--spacer) * 4)"></gz-loader>`;
  }

  protected renderDistributionLoader() {
    return html` <gz-loader rows="4" columns="1" style="margin-top: calc(var(--spacer) * 4)"></gz-loader>`;
  }

  protected renderScoreGaugeContent(companyState: RmCompanyState) {
    let disabled = false;
    if (!companyState.found) {
      disabled = true;
    }

    const activeGaugeColors = [
      getBulletColorForScore(ScoreLevels.LOW),
      getBulletColorForScore(ScoreLevels.MEDIUM),
      getBulletColorForScore(ScoreLevels.HIGH),
    ];
    const colorGradientOffsets = [ScoreLevels.LOW, ScoreLevels.MEDIUM, ScoreLevels.HIGH];
    const segmentsColors = ['--purple-chart-050', '--blue-chart-050'];
    const segmentsPercentages = [companyState.breakdown.baseScore, companyState.breakdown.modifiersScore];
    const severityText = getTextForScore(companyState.score);

    return html`
      <div
        class="${classMap({ 'd-flex flex-column align-items-center gap-2 mb-2': true, 'mr-2': !this.isWidgetSmall() })}"
      >
        <gz-multi-gauge
          .colors="${activeGaugeColors}"
          .segmentColors="${segmentsColors}"
          .segmentPercentages="${segmentsPercentages}"
          .severityText="${severityText}"
          value="${disabled ? 0 : companyState.score}"
          .gradientOffsets="${colorGradientOffsets}"
          ?disabled="${disabled}"
          hasLabels
          class="mb-2"
          percentageUnit=""
        >
        </gz-multi-gauge>

        <gz-text fontSize="12" class="text-center gauge-description-text${disabled ? ' disabled' : ''}">
          ${translateFn('Lower the configuration score by addressing what needs to be remediated.')}
          <gz-link
            target="${Target.BLANK}"
            href="https://www.bitdefender.com/business/support/en/77209-832326-how-the-company-risk-score-is-calculated.html"
          >
            ${translateFn('How is it calculated?')}
          </gz-link>
        </gz-text>
      </div>
    `;
  }

  protected renderScoreBreakdownContent(companyState: RmCompanyState) {
    const { baseScore, identitiesScore, resourcesScore, modifiersScore, modifiers } = companyState.breakdown;

    const { industryScore } = modifiers;

    const items = [
      {
        label: translateFn('Base score'),
        value: baseScore,
        color: 'var(--purple-chart-050)',
      },
      {
        label: translateFn('Resources score'),
        value: resourcesScore,
      },
      {
        label: translateFn('Identities score'),
        value: identitiesScore,
      },
      {
        label: translateFn('Modifiers score'),
        value: modifiersScore,
        color: 'var(--blue-chart-050)',
      },
      {
        label: translateFn('Industry modifier'),
        value: industryScore,
      },
    ].map(({ label, value, color }) => {
      const cells = !companyState.found
        ? [
            color
              ? html` <span
                  part="legend-square"
                  style="background-color: var(--grey-layout-202-disabled-icons);"
                ></span>`
              : html`<span part="space-square"></span>`,
            renderText('...'),
            renderNumber('--'),
          ]
        : [
            color
              ? html` <span part="legend-square" style="background-color: ${color};"></span>`
              : html`<span part="space-square"></span>`,
            renderText(label),
            renderNumber(`${value}`),
          ];

      return {
        cells,
      };
    });

    return html`
      <div class="d-flex flex-column score-breakdown-wrapper">
        <gz-title fontSize="13" class="text-uppercase mb-1">${translateFn('Score breakdown')}</gz-title>
        <gz-basic-table alignLastColumnRight .data="${items}"></gz-basic-table>
      </div>
    `;
  }

  protected renderDistributionContent(companyState: RmCompanyState) {
    const { findings, vulnerabilities, identityRisks } = companyState.distribution;

    const navigateToSection = (section: string) => {
      NavigationHelper.navigateToRiskManagement({
        company: { id: this.payload?.companyState?.companyId || '' },
        filters: [
          {
            [ColumnKeys.STATE]: { in: [State.ACTIVE] },
          },
          {
            [ColumnKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
          },
        ],
        section,
        newTab: true,
      });
    };

    const items = [
      {
        label: translateFn('Findings'),
        value: findings,
        handler: () => {
          navigateToSection('findings');
        },
        color: 'var(--green-teal-chart-400)',
      },
      {
        label: translateFn('Vulnerabilities'),
        value: vulnerabilities,
        handler: () => {
          navigateToSection('vulnerabilities');
        },
        color: 'var(--purple-chart-050)',
      },
      {
        label: translateFn('Identity risks'),
        value: identityRisks,
        handler: () => {
          navigateToSection('identity-risks');
        },
        color: 'var(--blue-chart-500)',
      },
    ].map(({ label, value, handler, color }) => {
      const cells = !companyState.found
        ? [
            html` <div part="row-label-text">${renderText('...')}</div>`,
            html` <div class="d-contents">
              <gz-progress
                valuenow="0"
                valuemax="100"
                progressColor="var(--grey-layout-202-disabled-icons)"
              ></gz-progress>
            </div>`,
            renderNumber('--'),
          ]
        : [
            html` <div part="row-label-text">${renderText(label, false, handler)}</div>`,
            html` <div class="d-contents">
              <gz-progress valuenow="${value}" valuemax="100" progressColor="${color}"></gz-progress>
            </div>`,
            renderNumber(`${value}%`),
          ];

      return {
        cells,
      };
    });

    return html`
      <div class="risk-distribution-wrapper">
        <gz-title fontSize="13" class="text-uppercase mb-1">${translateFn('Company risk distribution')}</gz-title>
        <gz-basic-table rowsClickable alignLastColumnRight .data="${items}"></gz-basic-table>
      </div>
    `;
  }

  private renderCompanySettings(): TemplateResult {
    return html`
      <gz-tooltip tooltiptext="${translateFn('Allow GravityZone to track suspicious user activity.')}">
        <gz-toggle
          .checked="${this.identityMonitoringToggle}"
          @toggleChange="${(e: CustomEvent) => this.handleIdentityMonitorToggle(e)}"
        >
          ${translateFn('Identity monitoring')}
        </gz-toggle>
      </gz-tooltip>
    `;
  }

  private async handleIdentityMonitorToggle(e: CustomEvent) {
    const companyId = this.payload.companyState?.companyId ?? '';
    if (!companyId) {
      return;
    }

    try {
      this.state = WidgetStateFrontEnd.IS_LOADING;

      const enabled = e.detail.checked;
      await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'Company',
        method: 'disableIorUserMonitoring',
        payload: {
          companyId,
          disabled: !enabled,
        },
      });

      this.identityMonitoringToggle = enabled;
    } finally {
      this.state = WidgetStateFrontEnd.IS_ACTIVE;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-company-state-widget': GzRmCompanyStateWidgetWebComponent;
  }
}
